import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { axiosPrivate } from "../../api/axios";
import API_URL from "../../utils/API_URL";
import { Box, Button, Card, CardContent, CardHeader, Chip, Typography, useTheme, Collapse, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CardMedia, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import { Icon } from "../icons";
import { useTranslation } from 'react-i18next';
import EditProjectDialog from './dialogs/EditProjectDialog';
import NewProjectDialog from './dialogs/NewProjectDialog';

const imageUrl = API_URL.replace("/backend", "");

const systemImageMapping = {
  LAYOUT: "/images/menu/4.png",
  APS: "/images/menu/1.png",
  SAFE: "/images/menu/2.png",
  MASTER: "/images/menu/5.png",
  PLAN: "/images/menu/3.png",
  SV: "/images/menu/4.png",
  AXIS: "/images/menu/AXIS.png",
  TPC: "/images/menu/TPC.jpg",
};

const systemGifMapping = {
  DT: "/images/DT2.jpg",
  LAYOUT: "/images/gif/DT.gif",
  APS: "/images/gif/APS.gif",
  SAFE: "/images/gif/SAFE.gif",
  MASTER: "/images/gif/MASTER.gif",
  PLAN: "/images/gif/APS.gif",
};

const ProjectPage = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentSystem, setCurrentSystem] = useState(null);
  const [systemEdit, setSystemEdit] = useState({
    system_name: "",
    system_description: "",
  });
  const [popover, setPopover] = useState({
    anchorEl: null,
    system: null,
    project: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageCacheKey, setImageCacheKey] = useState(Date.now());
  const [isUploadingLayout, setIsUploadingLayout] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ success: false, message: "" });
  const [isGifLoading, setIsGifLoading] = useState(false);
  const [error, setError] = useState("");
  const [manageProject, setManageProject] = useState(null);
  const [quota, setQuota] = useState({ project_limit: 0, user_limit: 0, project_count: 0, user_count: 0 });
  const theme = useTheme();

  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.is_admin === 1;

  useEffect(() => {
    fetchProjectList();
    fetchGroupQuota();
  }, []);

  const fetchProjectList = () => {
    axiosPrivate
      .post("/project/getProjectList")
      .then((res) => {
        const projectList = res.data.projects || [];
        setProjects(projectList);
        const initialExpandState = {};
        projectList.forEach((project) => {
          initialExpandState[project.project_id] = true;
        });
        setExpandedProjects(initialExpandState);
      })
      .catch((err) => {
        console.error("프로젝트 리스트 가져오기 실패:", err);
      });
  };

  const fetchGroupQuota = () => {
    axiosPrivate
      .post('/project/getGroupQuota')
      .then((res) => {
        const q = res.data || {};
        setQuota({
          project_limit: Number.isFinite(q.project_limit) ? q.project_limit : 0,
          user_limit: Number.isFinite(q.user_limit) ? q.user_limit : 0,
          project_count: Number.isFinite(q.project_count) ? q.project_count : 0,
          user_count: Number.isFinite(q.user_count) ? q.user_count : 0,
        });
      })
      .catch((err) => {
        console.error('그룹 한도 조회 실패:', err);
      });
  };

  const canCreateMoreProjects = quota.project_count < quota.project_limit;
  const subheaderText = `현재: ${quota.project_count} / 가능: ${quota.project_limit}`;

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleMasterData = (groupCode, projectCode, projectName) => {
    const url = `${window.location.origin}/master/machine/${groupCode}/${projectCode}`;
    const windowName = `master_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewItw = (groupCode, projectCode) => {
    const url = `${window.location.origin}/insightwin/${groupCode}/${projectCode}`;
    const windowName = `insightwin_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewSafe = (groupCode, projectCode) => {
    const url = `${window.location.origin}/safe/${groupCode}/${projectCode}`;
    const windowName = `safe_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewAps = (groupCode, projectCode) => {
    const url = `${window.location.origin}/aps/${groupCode}/${projectCode}/plan`;
    const windowName = `aps_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewPlan = (groupCode, projectCode) => {
    const url = `${window.location.origin}/plan/${groupCode}/${projectCode}`;
    const windowName = `plan_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleTpcPlan = (groupCode, projectCode) => {
    const url = `${window.location.origin}/tpcplan/${groupCode}/${projectCode}`;
    const windowName = `tpcplan_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleEditItw = (groupCode, projectCode, groupName, projectName, projectId) => {
    const url = `${window.location.origin}/edit/file/${groupCode}/${projectCode}?projectName=${projectName}&groupName=${groupName}&pid=${projectId}`;
    const windowName = `edit_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleEditSv = (groupCode, projectCode, groupName, projectName, projectId) => {
    const url = `${window.location.origin}/edit/file/${groupCode}/${projectCode}?projectName=${projectName}&groupName=${groupName}&pid=${projectId}&folder=sv`;
    const windowName = `edit_sv_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewEditItw = (group_code, project_code) => {
    // SPC 그룹인 경우 새로운 디자인의 페이지로 이동
    if (group_code === 'spc') {
      const url = `/insightwinSPC/${group_code}/${project_code}`;
      const windowName = `SPC_DT_${group_code}_${project_code}`;
      window.open(url, windowName);
      return;
    }

    if (group_code === 'tpc') {
      const url = `/insightwinTPC/${group_code}/${project_code}`;
      const windowName = `TPC_DT_${group_code}_${project_code}`;
      window.open(url, windowName);
      return;
    }

    const url = `/insightwinLEDT/${group_code}/${project_code}`;
    const windowName = `ITW_DT_${group_code}_${project_code}`;
    const features = "menubar=no,toolbar=no,status=no"; // 주소표시줄 숨김 (팝업)

    // 새 창 열기 (팝업 아님, 새 탭/창)
    window.open(url, windowName);
  };

  const handleViewLayoutTest = (groupCode, projectCode, projectId) => {
    const url = `${window.location.origin}/insightwinLE/${groupCode}/${projectCode}?pid=${projectId}`;
    const windowName = `layout_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewSimView = (groupCode, projectCode) => {
    const url = `${window.location.origin}/insightwinSV/${groupCode}/${projectCode}`;
    const windowName = `simview_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewAxis = (groupCode, projectCode) => {
    const url = `${API_URL}/axistool/${groupCode}/${projectCode}`;
    const windowName = `axis_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleOpenEditDialog = (system) => {
    setCurrentSystem(system);
    setSystemEdit({
      system_name: system.system_name || "",
      system_description: system.system_description || "",
    });
    setEditDialogOpen(true);
    setError("");
    setSelectedFile(null);
    setPreviewImage(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentSystem(null);
  };

  const handleOpenManageProject = (e, project) => {
    e.stopPropagation();
    setManageProject(project);
  };

  const handleCloseManageProject = (updated) => {
    setManageProject(null);
    if (updated) fetchProjectList();
  };

  const handleDeleteProject = (e, project) => {
    e.stopPropagation();
    if (!isAdmin) return;
    if (!window.confirm(`${project.project_name} 프로젝트를 삭제하시겠습니까?`)) return;
    axiosPrivate
      .delete(`/project/delete/${project.project_id}`)
      .then(() => {
        fetchProjectList();
      })
      .catch((err) => {
        console.error('프로젝트 삭제 실패:', err);
        alert(err.response?.data?.message || '프로젝트 삭제 중 오류가 발생했습니다.');
      });
  };

  // 새 프로젝트 생성 다이얼로그 열기
  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setError("");
  };

  const handleLayoutFileSelect = (event, project) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploadingLayout(true);
    setUploadStatus({ success: false, message: "" });

    const formData = new FormData();
    formData.append("layoutFile", file);
    formData.append("project_id", project.project_id);

    axiosPrivate
      .post(`/file/upload/${project.group_code}/${project.project_code}/layoutelement`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setUploadStatus({ success: true, message: "레이아웃이 성공적으로 업로드되었습니다." });
      })
      .catch((err) => {
        setUploadStatus({ success: false, message: err.response?.data?.message || "업로드 중 오류가 발생했습니다." });
      })
      .finally(() => {
        setIsUploadingLayout(false);
        event.target.value = null;
      });
  };

  const handleDownloadLayoutData = (groupCode, projectCode, projectName) => {
    axiosPrivate({
      url: `/file/download/${groupCode}/${projectCode}/layoutelement`,
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Layout_${projectCode}.json`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("레이아웃 데이터 다운로드 실패:", err);
        setError("데이터 다운로드에 실패했습니다. 서버 로그를 확인해주세요.");
      });
  };

  const handleSystemEditChange = (e) => {
    const { name, value } = e.target;
    setSystemEdit((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      setError("JPG 또는 PNG 이미지만 업로드할 수 있습니다.");
      setSelectedFile(null);
      setPreviewImage(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("이미지 크기는 5MB를 초과할 수 없습니다.");
      setSelectedFile(null);
      setPreviewImage(null);
      return;
    }

    setError("");
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const renderProjectCard = (project) => {
    return (
      <Grid size={{ xs: 12 }} key={project.project_id}>
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem",
            bgcolor: "var(--mui-palette-background-paper)",
            mb: 4,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              cursor: "pointer",
              bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
              "&:hover": {
                bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => toggleProjectExpand(project.project_id)}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flex: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {project.project_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.project_code}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isAdmin && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={(e) => handleOpenManageProject(e, project)}
                    sx={{ mr: 1 }}
                  >
                    관리
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={(e) => handleDeleteProject(e, project)}
                    sx={{ mr: 1 }}
                  >
                    삭제
                  </Button>
                </>
              )}
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleProjectExpand(project.project_id); }}>
                {expandedProjects[project.project_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={expandedProjects[project.project_id]}>
            {(() => {
              const dtSystem = project.systems.find((s) => s.type_name === "DT");
              const otherSystems = project.systems.filter((s) => s.type_name !== "DT");

              if (dtSystem) {
                return (
                  <Box
                    sx={{
                      p: 4,
                      background: theme.palette.mode === "dark" ? "var(--mui-palette-background-default)" : "var(--mui-palette-background-paper)",
                    }}
                  >
                    <Grid container spacing={4}>
                      <Grid size={{ xs: 12, lg: 4 }}>
                        <Card sx={{ height: "450px", display: "flex", flexDirection: "column" }}>
                          <Box sx={{ height: "200px", bgcolor: theme.palette.action.hover }}>
                            <CardMedia
                              component="img"
                              height="100%"
                              image={`${imageUrl}/StreamingAssets/${project.group_code}/${project.project_code}/le/Data/dt_image.png?v=${imageCacheKey}`}
                              alt="Digital Twin"
                              sx={{ objectFit: "contain" }}
                              onError={(e) => {
                                const defaultImgPath = "/images/default_dt_img.png";
                                if (e.target.src.includes(defaultImgPath)) {
                                  e.target.onerror = null;
                                  return;
                                }
                                if (!e.target.src.includes(".jpg")) {
                                  e.target.src = `${imageUrl}/StreamingAssets/${project.group_code}/${project.project_code}/le/Data/dt_image.jpg?v=${imageCacheKey}`;
                                } else {
                                  e.target.src = defaultImgPath;
                                }
                              }}
                            />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flexGrow: 1, p: 2.5 }}>
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                                  {dtSystem.system_name}
                                </Typography>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditItw(project.group_code, project.project_code, project.group_name, project.project_name, project.project_id)}
                                    disabled={!dtSystem.is_active}
                                    title={t('pages.project.layoutDataManagement')}
                                  >
                                    <Icon name="databaseIcon" style={{ width: '18px', height: '18px' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEditDialog({
                                      ...dtSystem,
                                      project_id: project.project_id,
                                      group_code: project.group_code,
                                      project_code: project.project_code,
                                    })}
                                    disabled={!dtSystem.is_active}
                                    title={t('pages.project.systemInfoEdit')}
                                  >
                                    <Icon name="editIcon" style={{ width: '20px', height: '20px' }} />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ minHeight: "60px", whiteSpace: "pre-line" }}>
                                {dtSystem.system_description || t('pages.project.noDescription')}
                              </Typography>
                            </Box>
                            <Box>
                              {dtSystem.is_active ? (
                                <Button variant="contained" color="success" fullWidth onClick={() => handleViewEditItw(project.group_code, project.project_code)} startIcon={<Icon name="monitorIcon" style={{ width: "18px", height: "18px" }} />}>
                                  {t('pages.project.openDigitalTwin')}
                                </Button>
                              ) : (
                                <Button variant="outlined" color="error" fullWidth disabled>
                                  {t('pages.project.disabled')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 8 }}>
                        <Grid container spacing={3}>
                          {otherSystems.map((system) => renderSystemItem(project, system))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                );
              } else {
                return (
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3} sx={{ height: "100%" }}>
                      {project.systems.map((system) => renderSystemItem(project, system))}
                    </Grid>
                  </Box>
                );
              }
            })()}
          </Collapse>
        </Box>
      </Grid>
    );
  };

  const renderSystemItem = (project, system) => {
    const imageUrl = systemImageMapping[system.type_name] || "/images/menu/default.png";
    const systemPermissionLevel = isAdmin ? 2 : system.system_permission_level || 0;
    const canEditSystem = isAdmin || systemPermissionLevel >= 2;

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={system.project_system_id}>
        <Card
          sx={{
            height: "219px",
            display: "flex",
            flexDirection: "column",
            opacity: system.is_active ? 1 : 0.5,
            transition: "all 0.2s ease-in-out",
            boxShadow: theme.shadows[2],
            borderRadius: "6px",
          }}
        >
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <CardMedia
              component="img"
              sx={{
                height: "110px",
                objectFit: "contain",
                backgroundColor: theme.palette.action.hover,
              }}
              image={imageUrl}
              alt={system.type_name}
            />
            {!system.is_active && (
              <Chip
                label={t('pages.project.disabled')}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  backgroundColor: "rgba(0,0,0,0.6)",
                  color: "white",
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", p: 2 }}>
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Chip label={system.system_name} variant="tonal" size="small" color={system.color || "default"} />
                <Box sx={{ mt: "-4px" }}>
                  {system.type_name === "SV" && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSv(project.group_code, project.project_code, project.group_name, project.project_name, project.project_id)}
                      disabled={!system.is_active}
                      title="SimView 파일 관리"
                    >
                      <Icon name="databaseIcon" style={{ width: "18px", height: "18px" }} />
                    </IconButton>
                  )}
                  {system.type_name === "LAYOUT" && (
                    <>
                      <IconButton
                        size="small"
                        component="label"
                        disabled={!system.is_active || isUploadingLayout}
                        title={t('pages.project.uploadLayoutFile')}
                      >
                        {isUploadingLayout ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Icon
                            name="mapDownloadIcon"
                            style={{
                              width: "18px",
                              height: "18px",
                              transform: "rotate(180deg)",
                            }}
                          />
                        )}
                        <input type="file" hidden accept=".json" onChange={(e) => handleLayoutFileSelect(e, project)} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDownloadLayoutData(project.group_code, project.project_code, project.project_name)} disabled={!system.is_active} title={t('pages.project.downloadLayoutFile')}>
                        <Icon name="mapDownloadIcon" style={{ width: "18px", height: "18px" }} />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleOpenEditDialog({
                        ...system,
                        project_id: project.project_id,
                        group_code: project.group_code,
                        project_code: project.project_code,
                      })
                    }
                    disabled={!system.is_active}
                    title={t('pages.project.systemInfoEdit')}
                  >
                    <Icon name="editIcon" style={{ width: "18px", height: "18px" }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ paddingInline: "2px" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {system.system_description || t('pages.project.noDescription')}
                </Typography>
              </Box>
            </Box>
            <Box>
              {system.is_active ? (
                <Box sx={{ width: "100%", display: "flex", gap: 1, flexDirection: "column" }}>
                  {system.type_name !== "LAYOUT" && (
                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (system.type_name === "APS") handleViewAps(project.group_code, project.project_code);
                        else if (system.type_name === "SAFE") handleViewSafe(project.group_code, project.project_code);
                        else if (system.type_name === "MASTER") handleMasterData(project.group_code, project.project_code, project.project_name);
                        else if (system.type_name === "PLAN") handleViewPlan(project.group_code, project.project_code);
                        else if (system.type_name === "SV") handleViewSimView(project.group_code, project.project_code);
                        else if (system.type_name === "TPC") handleTpcPlan(project.group_code, project.project_code);
                        else if (system.type_name === "AXIS") handleViewAxis(project.group_code, project.project_code);
                      }}
                      disabled={!canEditSystem && system.type_name === "MASTER"}
                    >
                      {t('pages.project.open')}
                    </Button>
                  )}
                  {system.type_name === "LAYOUT" && (
                    <Button variant="contained" fullWidth size="small" onClick={() => handleViewLayoutTest(project.group_code, project.project_code, project.project_id)} disabled={!canEditSystem}>
                      {t('pages.project.editLayout')}
                    </Button>
                  )}
                </Box>
              ) : (
                <Button variant="outlined" fullWidth size="small" disabled>
                  {t('pages.project.disabledSystem')}
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      </Grid>
    );
  };

  const renderEditSystemDialog = () => {
    return (
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('pages.project.editSystemInfo')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField autoFocus margin="dense" name="system_name" label={t('pages.project.systemName')} type="text" fullWidth variant="outlined" value={systemEdit.system_name} onChange={(e) => setSystemEdit(s => ({ ...s, system_name: e.target.value }))} sx={{ mb: 2 }} />

          <TextField margin="dense" name="system_description" label={t('pages.project.systemDescription')} type="text" fullWidth multiline rows={3} variant="outlined" value={systemEdit.system_description} onChange={(e) => setSystemEdit(s => ({ ...s, system_description: e.target.value }))} sx={{ mb: 2 }} />

          {currentSystem?.type_name === "DT" && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('pages.project.representativeImage')}
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                {t('pages.project.selectJPGPNGFile')}
                <input type="file" hidden accept="image/jpeg, image/png" onChange={(e) => { setError(""); setSelectedFile(e.target.files?.[0] || null); if (e.target.files?.[0]) { const r = new FileReader(); r.onloadend = () => setPreviewImage(r.result); r.readAsDataURL(e.target.files[0]); } }} />
              </Button>
              {previewImage && (
                <Box sx={{ mt: 2, height: 200, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: "hidden", bgcolor: theme.palette.action.hover }}>
                  <img src={previewImage} alt="미리보기" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{t('pages.project.cancel')}</Button>
          <Button onClick={() => {
            if (!currentSystem) return;
            const updatePromises = [];
            const textInfoPromise = axiosPrivate.post("/project/updateSystemInfo", {
              projectSystemId: currentSystem.project_system_id,
              systemName: systemEdit.system_name,
              systemDescription: systemEdit.system_description,
            });
            updatePromises.push(textInfoPromise);
            if (selectedFile) {
              const formData = new FormData();
              formData.append("files", selectedFile);
              formData.append("project_id", currentSystem.project_id);
              formData.append("group_code", currentSystem.group_code);
              formData.append("project_code", currentSystem.project_code);
              formData.append("upload_type", "dtimage");
              const imageUploadPromise = axiosPrivate.post("/file/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
              updatePromises.push(imageUploadPromise);
            }
            Promise.all(updatePromises)
              .then(() => { handleCloseEditDialog(); fetchProjectList(); setImageCacheKey(Date.now()); })
              .catch((err) => { console.error("시스템 정보 업데이트 실패:", err); setError(err.response?.data?.message || "시스템 정보 업데이트 중 오류가 발생했습니다."); });
          }} variant="contained">
            {t('pages.project.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%" }}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "0",
            bgcolor: theme.palette.mode === "dark" ? "var(--mui-palette-background-default)" : "var(--mui-palette-background-paper)",
          }}
        >
          <CardHeader
            title={t('pages.project.pageTitle')}
            subheader={subheaderText}
            sx={{
              "& .MuiCardHeader-title": { fontSize: "1.2rem", fontWeight: 600 },
            }}
            action={
              isAdmin && (
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreateDialog} disabled={!canCreateMoreProjects}>
                  {t('pages.project.newProject')}
                </Button>
              )}
          />
          <CardContent sx={{ flexGrow: 1, overflow: "auto", px: 6 }}>
            {uploadStatus.message && (
              <Alert severity={uploadStatus.success ? "success" : "error"} onClose={() => setUploadStatus({ success: false, message: "" })} sx={{ mb: 2 }}>
                {uploadStatus.message}
              </Alert>
            )}
            {projects.length > 0 ? <Grid container>{projects.map(renderProjectCard)}</Grid> : <Typography sx={{ textAlign: "center" }}>{t('pages.project.noProjects')}</Typography>}
          </CardContent>
        </Card>
      </Grid>
      <NewProjectDialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); fetchProjectList(); fetchGroupQuota(); }} onProjectAdded={() => { fetchProjectList(); fetchGroupQuota(); }} />
      {renderEditSystemDialog()}
      {!!manageProject && (
        <EditProjectDialog
          open={!!manageProject}
          onClose={(updated) => { handleCloseManageProject(updated); fetchGroupQuota(); }}
          project={manageProject}
        />
      )}
    </Grid>
  );
};

export default ProjectPage;
